import {
  AscCrosswordPuzzleWordResponse,
  AscCrosswordPuzzleBoardProps,
  AscCrosswordPuzzleWordCell,
} from "@ascendnow/shared";

export function generateCells(size: number): AscCrosswordPuzzleWordCell[] {
  return new Array(size * size).fill(null)?.map((_, index) => {
    const row = Math.floor(index / size);
    const column = index % size;
    return {
      id: index,
      row,
      column,
    };
  });
}

export function generateWordCells({
  wordsAcross,
  wordsDown,
  size,
  mode,
}: Required<
  Pick<
    AscCrosswordPuzzleBoardProps,
    "size" | "mode" | "wordsAcross" | "wordsDown"
  >
>): AscCrosswordPuzzleWordCell[] {
  const cells = generateCells(size);
  let wordCount = 0;
  wordsAcross?.forEach(({ beginningCellNumber, length, wordIndex, word }) => {
    const _length = (mode === "create" ? word?.length : length) || 0;
    for (let i = 0; i < _length; i++) {
      if (wordIndex && mode === "solve") {
        wordCount = wordIndex;
      } else if (i === 0) {
        wordCount++;
      }
      if (cells[beginningCellNumber + i]?.acrossWordIndex) {
        cells[beginningCellNumber + i].error = true;
      }

      cells[beginningCellNumber + i] = {
        ...cells[beginningCellNumber + i],
        acrossWordIndex: wordCount,
        isFirstCellOfWord: i === 0,
      };
    }
  });
  wordsDown?.forEach(({ beginningCellNumber, length, word, wordIndex }) => {
    const _length = (mode === "create" ? word?.length : length) || 0;
    for (let i = 0; i < _length; i++) {
      if (wordIndex && mode === "solve") {
        wordCount = wordIndex;
      } else if (
        i === 0 &&
        !(
          cells[beginningCellNumber]?.acrossWordIndex &&
          cells[beginningCellNumber]?.isFirstCellOfWord
        )
      ) {
        wordCount++;
      }
      if (cells[beginningCellNumber + i * size]?.downWordIndex) {
        cells[beginningCellNumber + i * size].error = true;
      }
      cells[beginningCellNumber + i * size] = {
        ...cells[beginningCellNumber + i * size],
        downWordIndex:
          wordIndex && mode === "solve"
            ? wordIndex
            : cells[beginningCellNumber]?.acrossWordIndex &&
              cells[beginningCellNumber]?.isFirstCellOfWord
              ? cells[beginningCellNumber]?.acrossWordIndex
              : wordCount,
        isFirstCellOfWord:
          cells[beginningCellNumber + i * size]?.isFirstCellOfWord || i === 0,
      };
    }
  });
  return cells;
}

export function groupCharactersByWordIndex(
  cells: AscCrosswordPuzzleWordCell[],
  characters: string[]
): AscCrosswordPuzzleWordResponse {
  return cells.reduce(
    (acc, cell) => {
      if (cell.acrossWordIndex) {
        if (!acc.wordsAcross[cell.acrossWordIndex]) {
          acc.wordsAcross[cell.acrossWordIndex] = {
            wordIndex: cell.acrossWordIndex,
            word: characters[cell.id] || "_",
          };
        } else {
          acc.wordsAcross[cell.acrossWordIndex].word +=
            characters[cell.id] || "_";
        }
      }
      if (cell.downWordIndex) {
        if (!acc.wordsDown[cell.downWordIndex]) {
          acc.wordsDown[cell.downWordIndex] = {
            wordIndex: cell.downWordIndex,
            word: characters[cell.id] || "_",
          };
        } else {
          acc.wordsDown[cell.downWordIndex].word += characters[cell.id] || "_";
        }
      }
      return acc;
    },
    { wordsAcross: [], wordsDown: [] } as AscCrosswordPuzzleWordResponse
  );
}

export function isCellIndexedDownward(
  cell: AscCrosswordPuzzleWordCell,
  cells: AscCrosswordPuzzleWordCell[]
): boolean {
  return (
    !!cell?.downWordIndex &&
    cells.some(
      (c) =>
        c?.isFirstCellOfWord &&
        c?.id !== cell?.id &&
        c?.acrossWordIndex === cell?.acrossWordIndex
    )
  );
}

export function getWordIndexForFirstCellOfWord(
  cell: AscCrosswordPuzzleWordCell,
  cells: AscCrosswordPuzzleWordCell[]
) {
  if (!cell?.isFirstCellOfWord) {
    return undefined;
  }

  if (isCellIndexedDownward(cell, cells)) {
    return cell?.downWordIndex;
  }

  return cell?.acrossWordIndex || cell?.downWordIndex;
}

export function mapWordsToCells({
  wordsAcross,
  wordsDown,
  size,
}: Required<
  Pick<AscCrosswordPuzzleBoardProps, "size" | "wordsAcross" | "wordsDown">
>) {
  const characters: string[] = [];
  const errors: boolean[] = [];

  wordsAcross?.forEach?.(({ word, beginningCellNumber }) => {
    if (!word?.length) {
      return;
    }
    for (let i = 0; i < word?.length; i++) {
      const cellNumber = beginningCellNumber + i;
      const cellRow = Math.floor(cellNumber / size);
      const beginningCellRow = Math.floor(beginningCellNumber / size);
      const isCellFilledWithAnotherCharacter =
        characters[cellNumber] && characters[cellNumber] !== word[i];
      const isValidCharacter = /[A-Z ]/i.test(word[i]);
      if (isCellFilledWithAnotherCharacter) {
        errors[cellNumber] = true;
        continue;
      }
      if (cellRow !== beginningCellRow || !isValidCharacter) {
        errors[cellNumber] = true;
      }
      characters[cellNumber] = word[i];
    }
  });
  wordsDown?.forEach?.(({ word, beginningCellNumber }) => {
    if (!word?.length) {
      return;
    }
    for (let i = 0; i < word?.length; i++) {
      const cellNumber = beginningCellNumber + i * size;
      const beginningCellRow = Math.floor(beginningCellNumber / size);
      const isCellFilledWithAnotherCharacter =
        characters[cellNumber] && characters[cellNumber] !== word[i];
      const isValidCharacter = /[A-Z ]/i.test(word[i]);

      if (isCellFilledWithAnotherCharacter) {
        errors[cellNumber] = true;
        continue;
      }
      if (beginningCellRow + word?.length > size || !isValidCharacter) {
        errors[cellNumber] = true;
      }
      characters[cellNumber] = word[i];
    }
  });

  return {
    characters,
    errors,
  };
}
